import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatAdminLabel, formatDisplayValue } from "@/lib/adminFormatters";
import { formatCurrency } from "@/lib/currency";


const statusTone = {
  awaiting_payment: "bg-amber-50 text-amber-700",
  payment_under_review: "bg-orange-50 text-orange-700",
  paid_confirmed: "bg-emerald-50 text-emerald-700",
  access_granted: "bg-emerald-50 text-emerald-700",
  access_under_review: "bg-amber-50 text-amber-700",
  access_revoked: "bg-red-50 text-red-700",
  review_needed: "bg-rose-50 text-rose-700",
};


const PaymentMetaLine = ({ label, value, testId }) => (
  <p className="text-sm leading-7 text-slate-700" data-testid={testId}>
    <span className="font-semibold text-slate-950">{label}:</span> {value}
  </p>
);


export const AdminPaymentHistorySection = ({ detail }) => {
  const payments = detail?.payments || [];
  const paymentOverview = detail?.payment_overview || {};

  return (
    <Card className="border-stone-200/80 bg-white/92 shadow-none" data-testid="admin-detail-payments-card">
      <CardHeader>
        <CardTitle className="text-[1.8rem] font-semibold leading-tight tracking-[-0.02em] text-slate-950" data-testid="admin-detail-payments-title">Payment history</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm leading-7 text-slate-600" data-testid="admin-detail-payments-scaffold-note">
          This section now supports existing EFT records and is prepared for future Yoco/card payment visibility inside applicant detail.
        </p>

        {!paymentOverview.yoco_enabled ? (
          <div className="rounded-[1.15rem] border border-stone-200 bg-[#fcfaf5] px-4 py-3 text-sm leading-7 text-slate-700" data-testid="admin-detail-payments-yoco-disabled-note">
            Yoco/card payments are not active yet in this environment.
          </div>
        ) : null}

        {!paymentOverview.has_yoco_records ? (
          <div className="rounded-[1.15rem] border border-dashed border-stone-300 bg-[#fcfaf5] px-4 py-3 text-sm leading-7 text-slate-600" data-testid="admin-detail-payments-yoco-empty-state">
            No Yoco/card payment records have been created for this applicant yet.
          </div>
        ) : null}

        {!payments.length ? (
          <div className="rounded-[1.15rem] border border-dashed border-stone-300 bg-[#fcfaf5] px-4 py-3 text-sm leading-7 text-slate-600" data-testid="admin-detail-payments-empty-state">
            No payment records are available for this applicant yet.
          </div>
        ) : null}

        {payments.map((payment) => {
          const amountLabel = payment.amount == null ? "—" : formatCurrency(payment.amount);
          const statusValue = payment.review_needed ? "review_needed" : payment.internal_status;
          return (
            <div key={payment.id} className="rounded-[1.25rem] border border-stone-200 bg-[#fcfaf5] p-4" data-testid={`admin-detail-payment-item-${payment.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="space-y-1">
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500" data-testid={`admin-detail-payment-type-${payment.id}`}>{formatAdminLabel(payment.payment_type)}</p>
                  <p className="text-base font-semibold text-slate-950" data-testid={`admin-detail-payment-title-${payment.id}`}>{payment.display_title}</p>
                </div>
                <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${statusTone[statusValue] || "bg-slate-100 text-slate-700"}`} data-testid={`admin-detail-payment-status-${payment.id}`}>
                  {formatAdminLabel(statusValue)}
                </span>
              </div>

              <div className="mt-4 grid gap-2 md:grid-cols-2">
                <PaymentMetaLine label="Provider" value={formatAdminLabel(payment.payment_provider)} testId={`admin-detail-payment-provider-${payment.id}`} />
                <PaymentMetaLine label="Method" value={formatAdminLabel(payment.payment_method)} testId={`admin-detail-payment-method-${payment.id}`} />
                <PaymentMetaLine label="Amount" value={amountLabel} testId={`admin-detail-payment-amount-${payment.id}`} />
                <PaymentMetaLine label="Currency" value={payment.currency || "—"} testId={`admin-detail-payment-currency-${payment.id}`} />
                <PaymentMetaLine label="Reference" value={payment.reference || "—"} testId={`admin-detail-payment-reference-${payment.id}`} />
                <PaymentMetaLine label="Checkout ID" value={payment.provider_checkout_id || "—"} testId={`admin-detail-payment-checkout-id-${payment.id}`} />
                <PaymentMetaLine label="Provider payment ID" value={payment.provider_payment_id || "—"} testId={`admin-detail-payment-provider-payment-id-${payment.id}`} />
                <PaymentMetaLine label="Provider status" value={payment.provider_status ? formatAdminLabel(payment.provider_status) : "—"} testId={`admin-detail-payment-provider-status-${payment.id}`} />
                <PaymentMetaLine label="Webhook confirmed" value={payment.webhook_confirmed_at ? formatDisplayValue(payment.webhook_confirmed_at) : "—"} testId={`admin-detail-payment-webhook-confirmed-${payment.id}`} />
                <PaymentMetaLine label="Webhook event" value={payment.last_webhook_event_id || "—"} testId={`admin-detail-payment-webhook-event-${payment.id}`} />
                <PaymentMetaLine label="Review reason" value={payment.review_reason_message || (payment.review_reason_code ? formatAdminLabel(payment.review_reason_code) : "—")} testId={`admin-detail-payment-review-reason-${payment.id}`} />
                <PaymentMetaLine label="Internal action" value={payment.internal_action_taken || "—"} testId={`admin-detail-payment-internal-action-${payment.id}`} />
                <PaymentMetaLine label="Created" value={payment.created_at ? formatDisplayValue(payment.created_at) : "—"} testId={`admin-detail-payment-created-${payment.id}`} />
                {payment.module_code ? <PaymentMetaLine label="Module" value={`${payment.module_code} • ${payment.module_title_snapshot || "—"}`} testId={`admin-detail-payment-module-${payment.id}`} /> : null}
              </div>

              {payment.review_needed ? (
                <p className="mt-4 text-sm leading-7 text-rose-700" data-testid={`admin-detail-payment-review-needed-${payment.id}`}>
                  This payment record requires admin review before any further automated action should occur.
                </p>
              ) : null}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};